// cleanup_convert_to_typescript_file_GENERICS
// cleanup_remove_external_libraries_GENERICS
// cleanup_strings_GENERICS

import axios from 'axios';
import moment from 'moment';
import FileSaver from 'file-saver';

import {
  CONTENT_TYPES,
  DEFAULT_URL_PREFIX,
  EMULATION_ERROR_CODE,
  ENVIRONMENTS,
  HEADERS,
  LOCALHOST,
  X_CHANNELS
} from 'constants.js';

const { QA, QA1, QA2, QA3, QA4 } = ENVIRONMENTS;

/** @todo Header hardcoded locally */
const { common } = axios.defaults.headers;

export const serverJSONParser = (response) => {
  const data = response?.data?.data || {};
  const { contentType, message: json } = data;

  if (!json || contentType !== CONTENT_TYPES.JSON) {
    return response;
  }

  try {
    response.data.data = JSON.parse(json);
  } catch (error) {
    // console.error('data is not JSON', error);
  }
  return response;
};

const leadBankBodyUrl = [
  '/cbe-authentication/user/getPwdComplexityRequirements',
  '/cbe-authentication/user/getSecurityQuestionsByLeadBank',
  '/cbe-authentication/public/user/getSecurityQuestions',
  '/cbe-branding/public/serviceMessage/getMessage'
];

/** @todo Urls that use a body param used until converted over */
export const leadBankBody = (request) => {
  if (request.headers) {
    /** @description 'Content-Type' requires at least a blank object for `request.data` */
    request.headers[HEADERS.CONTENT_TYPE] ||= CONTENT_TYPES.JSON;
    request.data ||= {};
  }

  if (!axios.defaults.baseURL) {
    request.url = request.url.replace('/', '/cbe-');
  }

  const detectedUrl = leadBankBodyUrl.includes(request.url);
  if (!detectedUrl) {
    return request;
  }

  const { data: origin = {} } = request;

  const data = {
    ...origin,
    leadBankCode: common[HEADERS.LEAD_BANK_CODE]
  };

  return { ...request, data };
};

/**
 * @description
 * The API won't send valid http status codes, unless the server is actually down (ie 500's)
 * This does not run in production mode for CRA
 * @link docs/images/apiErrorLogging.png
 */
export const errorReporter = (response) => {
  const success = response?.data?.success || true;
  /* eslint-disable no-console */
  if (!success) {
    // TODO: this response is always failing with 'User preferences not found' for development.
    if (response.config.url !== '/api/infrastructure/userUIPref/retrieve') {
      // console.log('%cAPI Error', 'color: tomato; font-size: 14px;');
      // console.log(`   url: ${response.config.url}`);
      // console.log(`   post data: ${response.config.data}`);
      // console.log('   post headers:');
      // console.table(response.config.headers);
      // console.log('   response data:');
      // console.table(response.data.data);
      // console.log('   response result:');
      // console.table(response.data.result);
      // console.log('%cend api error information', 'color: tomato; font-size: 14px;');
      // console.log('');
    }
  }
  /* eslint-enable no-console */
  return response;
};

/**
 * @description
 * The API requires a post instead of returning a link. Using headers to
 * cherry-pick thecorrect content-* needed and returning the blob.
 */
const fileSaveDetector = (response) => {
  const contentDisposition = response?.headers?.['content-disposition'];
  if (!contentDisposition) {
    return response;
  }
  const type = response?.headers?.['content-type'];
  const fileName = contentDisposition.match(/filename=(.+);*/i)[1].replace(';', '');
  const blob = new Blob([response.data], { type });
  FileSaver.saveAs(blob, fileName);
  return response;
};

/**
 * Handle forbidden actions in user emulation
 *
 * @param {object} res
 */
const handleEmulationForbiddenAction = (setSnackbarVisibility) => (res) => {
  const { data } = res.data;
  const code = data?.[0]?.code;
  const isEmulationError = code === EMULATION_ERROR_CODE;

  if (isEmulationError) {
    setSnackbarVisibility(true);
    return Promise.reject(Error(EMULATION_ERROR_CODE));
  }

  return res;
};

const handleEmulationLogout = (setSnackbarVisibility) => (res) => {
  const { responseURL } = res.request;
  const logoffRequestURL = '/api/authentication/session/logoff';
  if (responseURL.includes(logoffRequestURL)) {
    setSnackbarVisibility(false);
  }
  return res;
};

let requests = {};
const updateRequest = (request) => {
  if (!request) {
    return {
      calls: 1,
      lastCall: moment(),
      timeSinceLastCall: 0
    };
  }

  return {
    calls: request.calls + 1,
    lastCall: moment(),
    timeSinceLastCall: moment(moment().diff(request.lastCall)).format('mm:ss')
  };
};

const logActivity = (request) => {
  requests = {
    ...requests,
    [request.url]: updateRequest(requests[request.url])
  };
  // Filter out calls greater then one and put most called first
  const modified = Object.entries(requests)
    .filter((entry) => entry[1].calls > 1)
    .sort((a, b) => b[1].calls - a[1].calls)
    .reduce((combined, item) => ({ ...combined, [item[0]]: item[1] }), {});
  if (Object.keys(modified).length) {
    // eslint-disable-next-line no-console
    console.table(modified, ['calls', 'timeSinceLastCall']);
  }
  return request;
};

class AxiosFactory {
  constructor() {
    const baseURL = process.env.REACT_APP_URL_PREFIX ?? DEFAULT_URL_PREFIX;
    const isQA = [QA, QA1, QA2, QA3, QA4].includes(process.env.REACT_APP_ENV);
    const isLocal = window.location.host.includes(LOCALHOST);

    const envs = {
      qa: process.env.REACT_APP_ENVS_QA,
      qa2: process.env.REACT_APP_ENVS_QA2,
      qa3: process.env.REACT_APP_ENVS_QA3,
      qa4: process.env.REACT_APP_ENVS_QA4
    };

    axios.defaults.baseURL = baseURL;
    common[HEADERS.LEAD_BANK_CODE] = window.localStorage.getItem(HEADERS.LEAD_BANK_CODE);
    common[HEADERS.DOMAIN] =
      isQA && isLocal ? envs[process.env.REACT_APP_ENV] : window.location.host;

    axios.interceptors.response.use(serverJSONParser);
    axios.interceptors.request.use(leadBankBody);
    if (process.env.REACT_APP_LOG_REQUESTS) {
      axios.interceptors.request.use(logActivity);
    }

    axios.interceptors.response.use(fileSaveDetector);

    if (process.env.NODE_ENV !== 'production') {
      axios.interceptors.response.use(errorReporter);
    }
  }

  setAppConfig = ({ isNative }) => {
    common[HEADERS.X_CHANNEL] = isNative ? X_CHANNELS.MOBILE : X_CHANNELS.WEB;
  };

  /**
   * Return data, that is set through interceptors for further handling
   * @returns {object}
   */
  getData = () => axios.furtherHandling || {};

  static setLeadBankCode(code) {
    window.localStorage.setItem(HEADERS.LEAD_BANK_CODE, code);
    common[HEADERS.LEAD_BANK_CODE] = code;
  }

  static setupCommonErrorInterceptors = (handleSnackbarVisibilityFunc) => {
    axios.interceptors.response.use(handleEmulationForbiddenAction(handleSnackbarVisibilityFunc));
    axios.interceptors.response.use(handleEmulationLogout(handleSnackbarVisibilityFunc));
  };
}

export default AxiosFactory;
